import {
  ACTIVITY_TYPES,
  DEFAULT_CHOICE_OPTIONS,
  EMPTY_STRING,
  HABIT_CHOICE_TYPE,
  ROUTINE_CHOICE_OPTIONS
} from 'constants/general';
import { t } from 'services/i18n.service';
import Checklist from './Checklist';
import RandomChoices from './RandomChoices';
import Card from 'components/ui/Card';
import Alert from 'components/ui/Alert';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import {
  updateMoreOptionIsOfficeFriendly,
  updateMoreOptionChoiceType,
  updateMoreOptionChoices,
  updateMoreOptionCheckLists
} from 'store/reducer/modal/slice';
import { moreOptionActivityChoicesTabSelector } from 'store/reducer/modal/more-options.modal.selectors';
import EmptyState from 'components/ui/EmptyState';
import Select from 'components/ui/Select';
import Switch from 'components/ui/Switch';

const renderChoiceContent = (choiceType: string) => {
  if (choiceType === HABIT_CHOICE_TYPE.RANDOM) {
    return <RandomChoices />;
  }
  if (choiceType === HABIT_CHOICE_TYPE.CHECK_LIST) {
    return <Checklist />;
  }
  return (
    <EmptyState
      title={EMPTY_STRING}
      description={t('tabs_revamp.activity_choices_tab.no_choices_added')}
    />
  );
};

const ActivityChoicesTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const { activity, activityType } = useAppSelectorShallow(
    moreOptionActivityChoicesTabSelector
  );

  const isBreakingRoutine = activityType === ACTIVITY_TYPES.BREAKING;
  const choiceTypeOptions = (
    isBreakingRoutine ? DEFAULT_CHOICE_OPTIONS : ROUTINE_CHOICE_OPTIONS
  ).map(({ value, label }) => ({
    value,
    label: String(t(label)).toUpperCase()
  }));

  const isOfficeFriendly = activity?.is_office_friendly ?? false;
  const choiceType = activity?.choice_type || HABIT_CHOICE_TYPE.NONE;

  const handleOfficeFriendlyToggle = (checked: boolean) => {
    dispatch(updateMoreOptionIsOfficeFriendly(checked));
  };

  const handleChoiceTypeChange = (data?: string) => {
    if (!data) {
      return;
    }
    if (data === HABIT_CHOICE_TYPE.NONE) {
      dispatch(updateMoreOptionChoiceType(undefined));
      dispatch(updateMoreOptionChoices([]));
      dispatch(updateMoreOptionCheckLists([]));
      return;
    }

    dispatch(updateMoreOptionChoiceType(data));
    if (data === HABIT_CHOICE_TYPE.RANDOM) {
      dispatch(updateMoreOptionCheckLists([]));
    }
    if (data === HABIT_CHOICE_TYPE.CHECK_LIST) {
      dispatch(updateMoreOptionChoices([]));
    }
  };

  return (
    <div className='w-full flex flex-col ~gap-6/8'>
      {isBreakingRoutine && (
        <div className='flex flex-col ~gap-1/2'>
          <Switch
            checked={isOfficeFriendly}
            onChange={handleOfficeFriendlyToggle}
            label={t('is_office_friendly')}
            size='medium'
            labelPosition='right'
            disabled={activity?.include_in_every_break}
          />
          <Alert
            message={t('is_this_habit_safe_to_do_around_other_people')}
            variant='info'
            style='bordered'
            size='small'
          />
        </div>
      )}

      <Card variant='ghost' className='flex flex-col ~gap-2/2.5 !p-0'>
        <Card className='flex items-center ~gap-2/5'>
          <p className='~text-xs/base min-w-max'>{t('choice_type')}</p>
          <Select
            options={choiceTypeOptions}
            value={choiceType}
            onChange={(value) => handleChoiceTypeChange(value)}
            placeholder={t('choice_type')}
            size='sm'
            minWidth='250px'
          />
        </Card>

        <Card variant='ghost' className='!p-0'>
          {renderChoiceContent(choiceType)}
        </Card>
      </Card>
    </div>
  );
};

export default ActivityChoicesTab;
